/**
 * Eval Report Sidebar — right-side Sheet displaying A/B test results
 * with per-evaluator before/after score comparisons and deltas.
 *
 * Opened via a trigger button in the ab_results phase.
 */

import React from 'react';
import { BarChart3, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import type { ABTestSummary } from '@/lib/api/versionWorkflow';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  abTestSummary: ABTestSummary | null;
}

function resultBadge(result: string | null) {
  if (!result) return null;
  const lower = result.toLowerCase();
  if (lower.includes('pass') || lower.includes('improved')) {
    return (
      <Badge className="bg-green-100 text-green-700 dark:bg-green-950/50 dark:text-green-400 border-green-200 text-xs">
        Passed
      </Badge>
    );
  }
  if (lower.includes('regression') || lower.includes('fail')) {
    return (
      <Badge className="bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-400 border-red-200 text-xs">
        Regression
      </Badge>
    );
  }
  return (
    <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400 border-amber-200 text-xs">
      Inconclusive
    </Badge>
  );
}

function deltaColor(delta: number): string {
  if (delta > 0) return 'text-green-600 dark:text-green-400';
  if (delta < 0) return 'text-red-600 dark:text-red-400';
  return 'text-muted-foreground';
}

function scoreBarColor(score: number): string {
  if (score >= 70) return 'bg-green-500';
  if (score >= 50) return 'bg-amber-500';
  return 'bg-red-500';
}

const EvalReportSidebar: React.FC<Props> = ({
  open,
  onOpenChange,
  abTestSummary,
}) => {
  if (!abTestSummary) return null;

  const overallDelta =
    abTestSummary.staging_overall_avg != null && abTestSummary.production_overall_avg != null
      ? abTestSummary.staging_overall_avg - abTestSummary.production_overall_avg
      : null;

  const evaluators = abTestSummary.per_evaluator ?? [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto p-0 flex flex-col">
        {/* Header */}
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-border/40 shrink-0">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
            <SheetTitle className="text-base">Evaluation Report</SheetTitle>
            {overallDelta != null && (
              <span
                className={`ml-auto text-xs font-medium ${deltaColor(overallDelta)}`}
              >
                {overallDelta >= 0 ? '+' : ''}
                {overallDelta.toFixed(1)} avg
              </span>
            )}
          </div>
          <SheetDescription className="flex items-center gap-2">
            A/B test comparison
            {resultBadge(abTestSummary.result)}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {/* Overall score card */}
          {abTestSummary.staging_overall_avg != null && (
            <div className="rounded-xl border border-border/60 bg-card p-4 space-y-2">
              <p className="text-xs text-muted-foreground">Overall Score</p>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-semibold text-heading tabular-nums">
                  {abTestSummary.staging_overall_avg.toFixed(1)}
                </span>
                {overallDelta != null && (
                  <span className={`text-sm font-medium ${deltaColor(overallDelta)}`}>
                    {overallDelta >= 0 ? '+' : ''}
                    {overallDelta.toFixed(1)}
                  </span>
                )}
                <span className="text-xs text-muted-foreground">out of 100</span>
              </div>
            </div>
          )}

          {/* Per-evaluator breakdown */}
          {evaluators.length > 0 && (
            <div className="space-y-4">
              {evaluators.map((ev) => (
                <EvaluatorRow key={ev.evaluator_name} evaluator={ev} />
              ))}
            </div>
          )}

          {/* Empty state */}
          {evaluators.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-8">
              No evaluator scores available.
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

/* ── Evaluator Row ───────────────────────────────────────────────────────── */

interface EvaluatorRowProps {
  evaluator: {
    evaluator_name: string;
    production_avg: number;
    staging_avg: number;
    delta: number;
    delta_pct: number;
  };
}

const EvaluatorRow: React.FC<EvaluatorRowProps> = ({ evaluator }) => {
  const { evaluator_name, production_avg, staging_avg, delta } = evaluator;
  const name = evaluator_name
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

  const DeltaIcon =
    delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Minus;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm text-body">{name}</span>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground tabular-nums">
            {production_avg.toFixed(1)}
          </span>
          <span className="text-xs text-muted-foreground">&rarr;</span>
          <span className="text-sm font-medium text-heading tabular-nums">
            {staging_avg.toFixed(1)}
          </span>
          <span className={`flex items-center gap-0.5 text-xs font-medium ${deltaColor(delta)}`}>
            <DeltaIcon className="h-3 w-3" />
            {delta >= 0 ? '+' : ''}
            {delta.toFixed(1)}
          </span>
        </div>
      </div>

      {/* Before bar */}
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-muted-foreground w-10">Before</span>
          <div className="flex-1 h-1.5 rounded-full bg-secondary overflow-hidden">
            <div
              className="h-full rounded-full bg-muted-foreground/40 transition-all duration-500"
              style={{ width: `${Math.min(Math.max(production_avg, 0), 100)}%` }}
            />
          </div>
        </div>
        {/* After bar */}
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-muted-foreground w-10">After</span>
          <div className="flex-1 h-1.5 rounded-full bg-secondary overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${scoreBarColor(staging_avg)}`}
              style={{ width: `${Math.min(Math.max(staging_avg, 0), 100)}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EvalReportSidebar;
